import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import {
    ArrowRightOutlined,
    DownloadOutlined,
    PlayCircleOutlined,
    QuestionOutlined
} from '@ant-design/icons';
import {
    Form,
    Input,
    Typography,
    Space,
    Select,
    Switch,
    Tooltip,
    Alert,
    Badge
} from "antd";
import axios from "@common/axios";
import { isCancel } from 'axios';
import Tabs from "@common/Tabs";
import Tab from "@common/Tab";
import { BrandContext } from "@context/BrandContext";
import { UserContext } from "@context/UserContext";
import { ContentContext } from "@context/ContentContext";
import { debounce } from "lodash";
import withHover from "@common/higherOrderComponents/withHover";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import GlossaryTermDialog from "../glossary/GlossaryTermDialog";
import ListAntD from "@common/ListAntD";
import CircularLoader from "@common/CircularLoader";
import AccessibleInput from "@common/AccessibleInput";
import AccessibleSelect from "@common/AccessibleSelect";

const { Text, Title } = Typography;
const { Option } = Select;

export const SearchTabPanel = ({ searchTab, searchParameters, tabIndex }) => {
    const RESOURCE_LINK = {
        MEDIA: { key: 'mediaId', Component: MediaLink },
        CONCEPT: { key: 'conceptId', Component: ConceptLink },
        TOPIC: { key: 'topicId', Component: TopicLink },
        GLOSSARY: { key: 'glossaryTermId', Component: GlossaryLink },
        CURRICULUM: { key: '', Component: CurriculumContent },
    };
    const [showGlossary, setShowGlossary] = useState(false);
    const [glossaryTermId, setGlossaryTermId] = useState(null);

    const searchResources = (params, setData, setPaging, abortController) => {
        axios
            .get('/web/ui/search/searchResources', { params, signal: abortController.signal })
            .then(response => {
                setData(response.data.currPageContents);
                const { pageSize, totalResults, currPage } = response.data;
                setPaging({ currPage, pageSize, totalResults });
            })
            .catch(error => {
                if (!isCancel(error)) {
                    console.error('Error loading search resources', error);
                    displayToastMessage('Error loading search resources', error, 'error', undefined, 10000);
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    return (
        <>
            <h3 id={`search-tabpanel-${tabIndex}-label`}>Search results for {searchTab.tabName}</h3>
            <ListAntD
                pagingDependencies={[tabIndex]}
                disableTabbing={true}
                removeListPadding={true}
                pagingData={{ fetchFunction: searchResources, params: { ...searchParameters, searchTabIndex: tabIndex } }}
                renderItem={searchResult => {
                    const { key, Component } = RESOURCE_LINK[searchTab.resourceType];
                    const SelectedComponent =
                        searchTab.resourceType === 'CURRICULUM' ? Component : withHover(Component);
                    return (
                        <SelectedComponent
                            key={searchResult[key] || searchResult.id}
                            searchResult={searchResult}
                            searchTab={searchTab}
                            handleGlossaryTermId={setGlossaryTermId}
                            handleShowDialog={setShowGlossary}
                        />
                    );
                }}
                aria-label="Search results list"
            />
            {searchTab.resourceType === 'GLOSSARY' && (
                <GlossaryTermDialog
                    enableBreadcrumbs
                    open={showGlossary}
                    glossaryTermId={glossaryTermId}
                    onClose={() => {
                        setShowGlossary(false);
                        setGlossaryTermId(null);
                    }}
                />
            )}
        </>
    );
};

const withAccessibleLinkProps = handler => ({
    onClick: handler,
    onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler(e);
        }
    },
    href: '#'
});

export const MediaLink = ({ searchResult, hover }) => {
    const { setDialogMediaObject } = useContext(ContentContext);
    const isDownload = ['TEXTBOOK', 'WORKSHEET', 'COURSE_DOCUMENTATION'].includes(searchResult.mediaType);
    const baseType = searchResult.mediaType.charAt(0) + searchResult.mediaType.slice(1).toLowerCase();

    let title = `Open ${baseType} in dialog`;
    if (isDownload) {
        const isSolutions = searchResult.mediaSubType === 'WORKSHEET_SOLUTIONS';
        title = `Download ${baseType}${isSolutions ? ' Solutions' : ''}`;
    }

    const handleLoad = () => {
        if (['WIDGET', 'VIDEO', 'DESMOS'].includes(searchResult.mediaType)) {
            setDialogMediaObject({mediaId: searchResult.mediaId, mediaType: searchResult.mediaType, mediaSubType: searchResult.mediaSubType, mediaName: searchResult.mediaName, conceptId: searchResult.conceptId})
        } else if (isDownload) {
            openMediaDocument(searchResult.mediaId);
        }
    };

    return (
        <a
            {...withAccessibleLinkProps(handleLoad)}
            className="ch-link ch-no-hover flex smallGap flexAlignCentre"
        >
            <MathJaxHTMLContent showLoader={false} html={searchResult.mediaName} />
            {hover && (
                <span aria-hidden="true" className="icon-container">
                        {isDownload ? (
                            <DownloadOutlined aria-hidden="true" />
                        ) : (
                            <PlayCircleOutlined aria-hidden="true" />
                        )}
                    </span>
            )}
        </a>
    );
};

export const ConceptLink = ({ searchResult, searchTab, hover }) => {
    const label = `Maps to ${searchTab.tabName.slice(0, -1)}`;
    return (
        <a
            href={`/controlCentre/navigateSyllabuses.action?conceptId=${searchResult.conceptId}`}
            className="ch-link ch-no-hover flex padding--s smallGap flexAlignCentre"
        >
            <span>{searchResult.conceptName}</span>
            {hover && <ArrowRightOutlined aria-hidden="true" />}
        </a>
    );
};

export const TopicLink = ({ searchResult, searchTab, hover }) => {
    const label = `Maps to ${searchTab.tabName.slice(0, -1)}`;
    return (
        <a
            href={`/dashboard/changeCourseAndNavigateTopic.action?topicId=${searchResult.topicId}`}
            className="ch-link flex padding--s smallGap flexAlignCentre"
        >
            <span>{searchResult.topicName}</span>
            {hover && <ArrowRightOutlined aria-hidden="true" />}
        </a>
    );
};

export const GlossaryLink = ({ searchResult, hover, handleGlossaryTermId, handleShowDialog }) => {
    const handleLoadGlossaryTerm = () => {
        handleGlossaryTermId(searchResult.glossaryTermId);
        handleShowDialog(true);
    };

    return (
        <a
            {...withAccessibleLinkProps(handleLoadGlossaryTerm)}
            className="ch-link ch-no-hover flex padding--s smallGap flexAlignCentre"
        >
            <MathJaxHTMLContent showLoader={false} html={searchResult.glossaryTerm} />
        </a>
    );
};

export const CurriculumContent = ({ searchResult }) => {
    const brandConfig = useContext(BrandContext);
    const CurriculumLinkHover = withHover(CurriculumLink);

    return (
        <div role="group" aria-labelledby="curriculum-content-label" className="ch-curriculum-content">
            <Title level={4} id="curriculum-content-label" style={{ marginBottom: '4px' }}>
                {searchResult.curriculumCode}
            </Title>
            <Text>{searchResult.description}</Text>

            {/* Added a specific class to control list spacing */}
            <ul className="ch-curriculum-list">
                {searchResult.links.map(link => (
                    <li key={`${link.syllabusId}-${link.topicId}-${link.conceptId}`}>
                        <CurriculumLinkHover link={link} tabName={brandConfig.conceptName} />
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const CurriculumLink = ({ link, tabName = '', hover }) => {
    const label = `Maps to ${tabName}`;
    return (
        <a
            href={`/controlCentre/navigateSyllabuses.action?conceptId=${link.conceptId}`}
            className="ch-link ch-no-hover flex smallGap flexAlignCentre"
        >
            <span>
                {link.syllabusName} &gt; {link.topicName} &gt; {link.conceptName}
            </span>
            {hover && <ArrowRightOutlined aria-hidden="true" />}
        </a>
    );
};

const Search = props => {
    const { visible } = props;
    const { user } = useContext(UserContext);
    const brandConfig = useContext(BrandContext);

    const [tabIndex, setTabIndex] = useState(0);
    const [searchParameters, setSearchParameters] = useState({});
    const [loading, setLoading] = useState(true);
    const [isTabsLoading, setTabsLoading] = useState(true);
    const [searchTabs, setSearchTabs] = useState([]);
    const [searchTab, setSearchTab] = useState(null);
    const [syllabusGroups, setSyllabusGroups] = useState([]);
    const [syllabuses, setSyllabuses] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedSyllabusGroupId, setSelectedSyllabusGroupId] = useState(user.syllabusGroupId);
    const [selectedSyllabusId, setSelectedSyllabusId] = useState(user.syllabusId);
    const [selectedTopicId, setSelectedTopicId] = useState();
    const [showRequiredTermWarning, setShowRequiredTermWarning] = useState(true);
    const [searchForm] = Form.useForm();
    const [searchOnCurricCode, setSearchOnCurricCode] = useState(false);

    // load syllabus groups
    const loadSyllabusGroups = useCallback(() => {
        setLoading(true);
        axios.get('/web/ui/nav/syllabusesGroups')
            .then(response => {
                setSyllabusGroups(response.data);
            })
            .finally(() => setLoading(false));
    }, []);

    // load topics for a syllabus
    const loadTopics = useCallback((syllabusId) => {
        setLoading(true);
        axios.get(`/web/ui/nav/syllabus/${syllabusId}/topics`)
            .then(response => {
                setTopics(response.data);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadSyllabusGroups();
    }, [loadSyllabusGroups]);


    useEffect(() => {
        const found = syllabusGroups.find(g => g.syllabusGroupId === selectedSyllabusGroupId);
        if (found) {
            setSyllabuses(found.syllabuses);
            setSelectedSyllabusId(found.syllabuses[0].syllabusId);
            searchForm.setFieldsValue({ syllabusId: found.syllabuses[0].syllabusId });
        }
    }, [syllabusGroups, selectedSyllabusGroupId, searchForm]);

    useEffect(() => {
        if (selectedSyllabusId) {
            loadTopics(selectedSyllabusId);
        } else {
            setTopics([]);
            setSelectedTopicId(undefined);
        }
    }, [selectedSyllabusId, loadTopics]);

    // initialize form and search parameters when user is set
    useEffect(() => {
        if (user) {
            const fieldValues = {
                curriculumSearch: false,
                syllabusGroupId: user.syllabusGroupId,
                syllabusId: user.syllabusId,
                searchTerm: ''
            };
            setSearchParameters(fieldValues);
            searchForm.setFieldsValue(fieldValues);
        }
    }, [user, searchForm]);


    const loadSearchTabs = useCallback((abortController) => {
        setLoading(true);
        setTabsLoading(true);
        axios.get('/web/ui/search/searchTabs', {
            params: searchParameters,
            signal: abortController.signal
        })
            .then(response => {
                setSearchTabs(response.data);
                if (response.data.length > 0) {
                    const firstNonEmpty = response.data.findIndex(t => t.searchCount > 0);
                    const defaultIdx    = firstNonEmpty >= 0 ? firstNonEmpty : 0;
                    setTabIndex(defaultIdx);
                    setSearchTab(response.data[defaultIdx]);
                    setShowRequiredTermWarning(!searchParameters.searchTerm);
                }
            })
            .catch(error => {
                if (!isCancel(error)) {
                    console.error('Error loading search tabs', error);
                }
            })
            .finally(() => {
                setLoading(false);
                setTabsLoading(false);
            });
    }, [searchParameters]);

    useEffect(() => {
        if (searchTabs.length && tabIndex >= 0) {
            setSearchTab(searchTabs[tabIndex]);
        }
    }, [searchTabs, tabIndex]);

    const debouncedSearchTabs = useMemo(
        () => debounce(loadSearchTabs, 600),
        [loadSearchTabs]
    );

    useEffect(() => {
        if (Object.keys(searchParameters).length) {
            const ac = new AbortController();
            debouncedSearchTabs(ac);
            return () => {
                ac.abort();
                debouncedSearchTabs.cancel();
            };
        }
    }, [searchParameters, debouncedSearchTabs]);

    useEffect(() => {
        if (visible && searchTabs.length === 0 && Object.keys(searchParameters).length) {
            const ac = new AbortController();
            debouncedSearchTabs(ac);
            return () => {
                ac.abort();
                debouncedSearchTabs.cancel();
            };
        }
    }, [visible, searchTabs.length, searchParameters, debouncedSearchTabs]);

    const handleSearchFormChange = () => {
        const values = searchForm.getFieldsValue(true);
        setSelectedSyllabusGroupId(values.syllabusGroupId);
        setSelectedSyllabusId(values.syllabusId);
        setSelectedTopicId(values.topicId);
        setSearchParameters(values);
    };

    const handleSearchCurricCode = checked => {
        setSearchOnCurricCode(checked);
        searchForm.setFieldsValue({ curriculumSearch: checked });
        handleSearchFormChange();
    };

    const handleSearchTermChange = e => {
        searchForm.setFieldsValue({ searchTerm: e.target.value });
        handleSearchFormChange();
    };

    const changeKey = val => {
        setTabIndex(val);
        setSearchTab(searchTabs[val]);
    };

    return (
        <>
            <h2 id="drawer-title">Search resources</h2>
            <Form
                className="flex padding-t--s padding-m--s"
                form={searchForm}
                initialValues={searchParameters}
                autoComplete="off"
                onValuesChange={handleSearchFormChange}
            >
                <Space orientation="vertical" size="small" style={{ width: '95%' }}>
                    <Form.Item
                        label={brandConfig.syllabusGroupName}
                        name="syllabusGroupId"
                    >
                        <Select getPopupContainer={trigger => trigger.parentElement}>
                            {syllabusGroups.map(group =>
                                <Option
                                    key={group.syllabusGroupId}
                                    value={group.syllabusGroupId}
                                >
                                    {group.syllabusGroupName}
                                </Option>
                            )}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={brandConfig.syllabusName}
                        name="syllabusId"
                    >
                        <AccessibleSelect
                            placeholder={`Select ${brandConfig.syllabusName}`}
                            options={syllabuses.map(sys => ({
                                value: sys.syllabusId,
                                label: sys.syllabusName
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={brandConfig.topicName}
                        name="topicId"
                    >
                        <AccessibleSelect
                            disabled={searchOnCurricCode}
                            placeholder={`Select ${brandConfig.topicName}`}
                            options={topics.map(t => ({
                                label: t.topicName,
                                value: t.topicId
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Search Keyword"
                        name="searchTerm"
                    >
                        <AccessibleInput onChange={handleSearchTermChange}/>
                    </Form.Item>

                    {brandConfig.enabledCurriculumSearching && (
                        <Form.Item
                            name="curriculumSearch"
                            label="Search curriculum code"
                            valuePropName="checked"
                        >
                            <Switch id="curriculumSearch" onChange={handleSearchCurricCode} aria-label="Search curriculum code"><span className="ch-accessibility-label">Search curriculum code</span></Switch>
                        </Form.Item>
                    )}
                </Space>
            </Form>

            <div className="flexMax flexColumn">
                {(loading || searchTabs.length === 0) && (
                    <CircularLoader pageCentered={true} displayInElement={true}/>
                )}

                {!loading && searchTabs.length > 0 && (
                    <div className="ch-search-results-wrapper margin-t--m margin-l--m">
                        {showRequiredTermWarning && (
                            <Alert
                                title="Results require a search keyword to be provided"
                                type="warning"
                                showIcon
                                className="margin-b--m ch-search-alert"
                            />
                        )}
                        <Tabs onChange={changeKey} overrideKey={tabIndex} className="ch-search-tabs flexMax">
                            {searchTabs.map((tab, idx) => (
                                <Tab
                                    key={idx}
                                    tab={
                                        <span className="flex smallGap flexAlignCentre">
                                            {tab.tabName}
                                             <Badge count={tab.searchCount}/>
                                        </span>
                                    }
                                    tabpanelclassname="ch-search-items-blk"
                                    disabled={!tab.searchCount}
                                    value={idx}
                                >
                                    <SearchTabPanel
                                        key={tab.tabName + '-' + idx}
                                        searchTab={tab}
                                        searchParameters={searchParameters}
                                        tabIndex={idx}
                                    />
                                </Tab>
                            ))}
                        </Tabs>
                    </div>
                )}
            </div>
        </>
    );
};

export default Search;